import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'

import { type FC, useContext } from 'react'
import { ChevronDown } from 'react-bootstrap-icons'

import { Trigger as RadixAccordionTrigger } from '@radix-ui/react-accordion'

import { BlockContext } from '../BlockContext'
import type { BlockHeaderBlocksTypes, BlockHeaderProps } from '../types'

import { BlockHeaderAction } from './Action'
import { BlockHeaderActions } from './Actions'
import { BlockHeaderCheckbox } from './Checkbox'
import { TypographySection } from './TypographySection'

/**
 * Block.Title - Title of a block or accordion item
 *
 * Automatically detects the context (static vs accordion) and behaves accordingly.
 * In an accordion, it becomes a clickable trigger. In a static block, it's a simple header.
 *
 * Supports adding checkbox and action buttons outside the trigger zone via
 * Block.Title.Checkbox and Block.Title.Actions. Elements are automatically organized in HLayouts.
 *
 * @example
 * ```tsx
 * // Simple title in an accordion
 * <Block.Title>My title</Block.Title>
 *
 * // With checkbox on the left
 * <Block.Title>
 *   <Block.Title.Checkbox checked={false} onChange={() => {}} />
 *   My title
 * </Block.Title>
 *
 * // With actions on the right
 * <Block.Title>
 *   My title
 *   <Block.Title.Actions>
 *     <Block.Title.Action label="Action" onClick={() => {}} />
 *   </Block.Title.Actions>
 * </Block.Title>
 *
 * // With checkbox and actions
 * <Block.Title>
 *   <Block.Title.Checkbox checked={false} onChange={() => {}} />
 *   My title
 *   <Block.Title.Actions>
 *     <Block.Title.Action icon={{ component: CheckCircle }} onClick={() => {}} />
 *     <Block.Title.Action icon={{ component: DashCircle }} onClick={() => {}} />
 *   </Block.Title.Actions>
 * </Block.Title>
 * ```
 */
const BlockHeaderBase: FC<BlockHeaderProps> = ({ children }) => {
  const context = useContext(BlockContext)
  const isStatic = Boolean(context.disabled)

  const {
    BlockHeaderCheckbox: checkbox,
    BlockHeaderActions: actions,
    content,
    ...otherBlocks
  } = getTypedBlocksByTypes<BlockHeaderBlocksTypes>(children)

  const triggerChildren = (
    <>
      {content}
      {Object.values(otherBlocks)}
    </>
  )

  return (
    <div className={cn('block-trigger-wrapper', isStatic && 'block-trigger-wrapper--static')}>
      {checkbox}
      <RadixAccordionTrigger
        className={cn('block-trigger block-trigger-title', isStatic && 'block-trigger--static')}
      >
        {triggerChildren}
      </RadixAccordionTrigger>
      {actions}
      {!isStatic && (
        <RadixAccordionTrigger className="block-trigger">
          <ChevronDown className="block-chevron" aria-hidden />
        </RadixAccordionTrigger>
      )}
    </div>
  )
}

BlockHeaderBase.displayName = 'BlockHeader'

export const BlockHeader = Object.assign(BlockHeaderBase, {
  Action: BlockHeaderAction,
  Actions: BlockHeaderActions,
  Checkbox: BlockHeaderCheckbox,
  TypographySection: TypographySection,
})
